import React from 'react';

// Decorative inline-SVG landscape cover (no hosted images). A small library of
// templates styled by a palette of colors. Used on TripCard (and later
// per-journey covers). See design-handoff.md B.1.

type Palette = { sky1: string; sky2: string; h1: string; h2: string; h3: string };

const PALETTES: Record<string, Palette> = {
  alpine: { sky1: '#cfe8f7', sky2: '#eef8ff', h1: '#6f9c8e', h2: '#4a7468', h3: '#2f5249' },
  coastal: { sky1: '#bfe3ef', sky2: '#eafafd', h1: '#6fb1bf', h2: '#3f8ea0', h3: '#2c6b7a' },
  forest: { sky1: '#dcebd2', sky2: '#eef7e6', h1: '#74a05c', h2: '#4f7a3f', h3: '#365828' },
  sand: { sky1: '#fbe7c6', sky2: '#fff6e6', h1: '#e0c08a', h2: '#c89d63', h3: '#a87d45' },
  dusk: { sky1: '#e7d3ec', sky2: '#fbeef3', h1: '#8f7aa6', h2: '#6c5784', h3: '#4c3c61' },
  meadow: { sky1: '#d9ecd0', sky2: '#f0f8ea', h1: '#86b06a', h2: '#5f8c45', h3: '#436730' },
};

function paletteFor(key: string): Palette {
  return PALETTES[key] || PALETTES.alpine;
}

/** Returns the layered silhouette <path>s for a template. viewBox 0 0 400 200. */
function shapes(template: string, p: Palette): React.ReactNode {
  switch (template) {
    case 'peaks':
      return (
        <>
          <path d="M0 200 L80 90 L150 160 L230 60 L300 150 L400 80 L400 200 Z" fill={p.h3} />
          <path d="M0 200 L120 130 L210 175 L320 110 L400 165 L400 200 Z" fill={p.h2} />
          <path d="M0 200 L160 165 L280 185 L400 160 L400 200 Z" fill={p.h1} />
        </>
      );
    case 'coast':
      return (
        <>
          <path d="M0 150 Q120 120 240 145 T400 140 L400 200 L0 200 Z" fill={p.h2} />
          <path d="M0 178 Q140 165 260 178 T400 175 L400 200 L0 200 Z" fill={p.h1} />
          <path d="M0 200 L400 200 L400 188 Q200 198 0 188 Z" fill={p.h3} opacity={0.5} />
        </>
      );
    case 'dunes':
      return (
        <>
          <path d="M0 165 Q100 130 200 160 T400 155 L400 200 L0 200 Z" fill={p.h2} />
          <path d="M0 185 Q120 160 240 182 T400 180 L400 200 L0 200 Z" fill={p.h1} />
        </>
      );
    case 'city':
      return (
        <>
          <path d="M0 200 L0 150 L40 150 L40 110 L80 110 L80 150 L120 150 L120 90 L160 90 L160 150 L200 150 L200 120 L240 120 L240 150 L300 150 L300 100 L340 100 L340 150 L400 150 L400 200 Z" fill={p.h2} />
          <path d="M0 200 L0 175 L60 175 L60 160 L130 160 L130 180 L210 180 L210 165 L290 165 L290 178 L400 178 L400 200 Z" fill={p.h1} />
        </>
      );
    case 'hills':
    default:
      return (
        <>
          <path d="M0 160 Q100 110 200 155 T400 150 L400 200 L0 200 Z" fill={p.h3} />
          <path d="M0 175 Q120 135 240 170 T400 165 L400 200 L0 200 Z" fill={p.h2} />
          <path d="M0 190 Q140 165 260 186 T400 184 L400 200 L0 200 Z" fill={p.h1} />
        </>
      );
  }
}

export interface SceneCoverProps {
  template: string;
  palette: string;
  className?: string;
  style?: React.CSSProperties;
  /** Desaturate for past trips (design-handoff.md StatusPill `past`). */
  muted?: boolean;
}

const SceneCover: React.FC<SceneCoverProps> = ({ template, palette, className, style, muted }) => {
  const p = paletteFor(palette);
  const gradientId = `sky-${palette}-${template}`;
  return (
    <svg
      viewBox="0 0 400 200"
      preserveAspectRatio="xMidYMid slice"
      className={className}
      style={{ display: 'block', width: '100%', height: '100%', filter: muted ? 'saturate(0.45)' : undefined, ...style }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={p.sky1} />
          <stop offset="100%" stopColor={p.sky2} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="400" height="200" fill={`url(#${gradientId})`} />
      <circle cx="320" cy="48" r="22" fill="#ffffff" opacity={0.7} />
      {shapes(template, p)}
    </svg>
  );
};

export default SceneCover;
